import { useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { CmsSectionEditor, CmsCustomizedBadge, type CmsSectionEditorProps } from './CmsSectionEditor';
import type { CmsPageDef } from '../content/registry';
import { SINGLE_FALLBACKS, type CmsListItem } from '../content/fallbacks';
import type { AdminCmsSection, CmsSectionValue } from '../api/cms';

/**
 * Shared chrome for the two marketing-CMS editors (admin Cms.tsx, reseller
 * Content.tsx) — the standard two-pane CMS layout instead of one endless
 * scroll of ~50 open editors:
 *
 *   - LEFT: one entry per page (from the registry), with a customized/total
 *     count. Vertical sticky rail on desktop, horizontal scroll strip on mobile.
 *   - RIGHT: only the selected page's sections, each a COLLAPSED accordion row
 *     (label + Customized badge + key); expand to edit. A filter box narrows
 *     the list when a page has many sections.
 *
 * Which sections appear and how they save stays entirely with the caller —
 * this component is layout only.
 */
export function CmsEditorLayout({
  pages,
  byKey,
  fallbackListFor,
  save,
  clear,
  onSaved,
}: {
  pages: CmsPageDef[];
  byKey: Map<string, AdminCmsSection>;
  /** Resolve a LIST section's fallback items (reseller editor overrides some). */
  fallbackListFor: (key: string) => CmsListItem[];
  save: CmsSectionEditorProps['save'];
  clear: CmsSectionEditorProps['clear'];
  onSaved: CmsSectionEditorProps['onSaved'];
}) {
  const [activeKey, setActiveKey] = useState(pages[0]?.key ?? '');
  const [filter, setFilter] = useState('');
  const page = pages.find((p) => p.key === activeKey) ?? pages[0];
  if (!page) return null;

  const q = filter.trim().toLowerCase();
  const visible = q
    ? page.sections.filter((s) => s.label.toLowerCase().includes(q) || s.key.toLowerCase().includes(q))
    : page.sections;

  return (
    <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-6 lg:items-start">
      <nav
        aria-label="CMS pages"
        className="flex lg:flex-col gap-1 overflow-x-auto nice-scroll pb-2 lg:pb-0 mb-4 lg:mb-0 lg:sticky lg:top-20"
      >
        {pages.map((p) => {
          const customized = p.sections.filter((s) => byKey.has(s.key)).length;
          const active = p.key === page.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setActiveKey(p.key);
                setFilter('');
              }}
              className={
                'shrink-0 lg:shrink lg:w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between gap-3 ' +
                (active
                  ? 'bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm font-medium'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800')
              }
            >
              <span className="truncate">{p.label}</span>
              <span
                className={
                  'shrink-0 text-[11px] tabular-nums px-1.5 py-px rounded-full ' +
                  (customized > 0
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400')
                }
                title={`${customized} of ${p.sections.length} sections customized`}
              >
                {customized}/{p.sections.length}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-zinc-950 dark:text-zinc-50">{page.label}</h2>
          {page.sections.length > 5 && (
            <label className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter sections…"
                className="pl-8 pr-3 py-1.5 w-48 rounded-lg text-sm border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </label>
          )}
        </div>

        <div className="space-y-2.5">
          {visible.length === 0 && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400 py-6 text-center">No sections match “{filter}”.</p>
          )}
          {visible.map((section) => (
            <details
              key={section.key}
              className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 open:shadow-sm"
            >
              <summary className="cursor-pointer list-none px-4 sm:px-5 py-3 flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
                <span className="flex items-center gap-2.5 min-w-0">
                  <ChevronRight
                    className="w-4 h-4 shrink-0 text-zinc-400 transition-transform duration-150 group-open:rotate-90"
                    aria-hidden="true"
                  />
                  <span className="font-medium text-zinc-950 dark:text-zinc-50 truncate">{section.label}</span>
                  <CmsCustomizedBadge customized={byKey.has(section.key)} />
                </span>
                <span className="text-xs text-zinc-400 dark:text-zinc-500 mono hidden sm:block shrink-0">{section.key}</span>
              </summary>
              <div className="px-4 sm:px-5 pb-5 border-t border-zinc-100 dark:border-zinc-900">
                <CmsSectionEditor
                  bare
                  spec={section}
                  existing={byKey.get(section.key)}
                  fallbackSingle={(SINGLE_FALLBACKS[section.key] ?? {}) as CmsSectionValue}
                  fallbackList={fallbackListFor(section.key)}
                  save={save}
                  clear={clear}
                  onSaved={onSaved}
                />
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

